import { useEffect, useState } from 'react';

/**
 * Pick a readable ink for an element that paints itself with a colour we can't
 * know at build time — typically a CTA whose background is
 * `var(--primary-color)`.
 *
 * Why measure the DOM instead of resolving the token?
 * `resolveCssColor` (utils/readableTextColor) reads custom properties off
 * `:root`, but storefront sections routinely re-declare `--primary-color` on
 * their own wrapper (every per-template variant block does this). A CTA nested
 * inside such a section therefore paints with a colour that never appears on
 * `:root`. Reading the element's *own* computed background is the only way to
 * see what it will actually render.
 *
 * The audit that prompted this: white-on-primary failed WCAG AA on 23
 * template/colour combinations — Vital, Depot and Sakura set a literal
 * `#FFFFFF` primary on some sections (white text on white, 1.0:1), Kinetic
 * `#F5FF3D` (1.09:1), Sugar and Challenger lime (1.18:1), Aurora mint
 * (1.27:1). Verdure shipped a cream primary that rendered "Contact Us" as
 * near-invisible.
 *
 * Returns '#fff' or '#111', whichever has more contrast against the measured
 * background. A background that is missing or mostly transparent keeps
 * `fallback`, since the element is then sitting on whatever is behind it and
 * the caller's own choice is the better guess.
 */
export function useContrastInk(
  el: HTMLElement | null,
  fallback: string = '#fff',
): string {
  const [ink, setInk] = useState(fallback);

  useEffect(() => {
    if (!el || typeof window === 'undefined') return;

    const read = () => {
      const parts = getComputedStyle(el).backgroundColor.match(/[\d.]+/g);
      if (!parts || parts.length < 3) return;
      const alpha = parts.length > 3 ? Number(parts[3]) : 1;
      if (alpha < 0.5) { setInk(fallback); return; }

      const toLinear = (v: number) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
      const [r, g, b] = parts.slice(0, 3).map((v) => toLinear(Number(v) / 255));
      const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;

      const contrastWithWhite = 1.05 / (L + 0.05);
      const contrastWithBlack = (L + 0.05) / 0.05;
      setInk(contrastWithWhite >= contrastWithBlack ? '#fff' : '#111');
    };

    read();

    // Re-measure when the palette changes under us: template switch, builder
    // colour edit, or a section re-declaring its tokens.
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
    const section = el.closest('section');
    if (section) observer.observe(section, { attributes: true, attributeFilter: ['style', 'class'] });

    return () => observer.disconnect();
  }, [el, fallback]);

  return ink;
}

export default useContrastInk;
